import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  CardMedia,
  Modal,
  Box,
  IconButton,
} from "@mui/material";
import { CartItem, addItem, Variant } from "../store/cartSlice"; // Adjust import as needed
import { useDispatch } from "react-redux";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined"; // Import the cart icon
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ChildCareOutlinedIcon from "@mui/icons-material/ChildCareOutlined";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

interface ItemTileProps {
  item: {
    id: string;
    name: string;
    type: string;
    isActive: boolean;
    isSaleActive: boolean; // Add the flag for sales
    images?: string[];
    variants: Variant[];
  };
}

const ItemTile: React.FC<ItemTileProps> = ({ item }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null); // Store selected variant
  const [quantity, setQuantity] = useState<number>(1); // Quantity state
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0); // Track current image index
  const images = selectedVariant?.imageUrl
    ? [selectedVariant.imageUrl, ...(item.images || [])]
    : item.images || [];

  // Dynamically extract color, design, material from variants and use color name along with color code
  const colors = item.variants.map((variant) => ({
    colorCode: variant.colorCode || "#FFFFFF", // Default to white
    colorName: variant.color || "Unknown", // Assuming you have a colorName field in Firestore
  }));

  useEffect(() => {
    if (item.variants && item.variants.length > 0) {
      setSelectedVariant(item.variants[0]); // Set the first variant as default if variants exist
      setSelectedColor(item.variants[0].colorCode || "#FFFFFF");
    }
  }, [item]);

  // Update the selected variant when the user selects a color
  const handleColorSelection = (color: string) => {
    setSelectedColor(color);
    const matchingVariant = item.variants.find(
      (variant) => variant.colorCode === color
    );
    setSelectedVariant(matchingVariant || null);
    setCurrentImageIndex(0);
  };

  const handleAddItem = () => {
    if (selectedVariant) {
      const itemWithDetails: CartItem = {
        id: item.id,
        name: item.name,
        type: item.type,
        isActive: item.isActive,
        isSaleActive: item.isSaleActive,
        selectedVariant: {
          ...selectedVariant,
          quantity,
        },
      };
      dispatch(addItem(itemWithDetails));
      handleClose();
    }
  };

  const handleOpen = () => {
    setOpen(true);
    if (!selectedVariant && item.variants.length > 0) {
      setSelectedVariant(item.variants[0]); // Set the first variant as selected by default
      setSelectedColor(item.variants[0].colorCode || "#FFFFFF");
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedVariant(item.variants[0]); // Reset color on close
    setSelectedColor(item.variants[0].colorCode || "#FFFFFF");
    setQuantity(1); // Reset quantity on close
    setCurrentImageIndex(0);
  };

  // Carousel Functions
  const handlePrevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const increaseQuantity = () => {
    if (selectedVariant && quantity < selectedVariant.availableQuantity) {
      setQuantity(quantity + 1);
    }
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          cursor: "pointer",
        }}
      >
        <Card
          variant="outlined"
          style={{
            margin: "10px",
            width: "350px",
            height: "400px",
            borderRadius: "20px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            overflow: "hidden",
            position: "relative",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
            backgroundColor: "rgba(255, 255, 255, 0.4)",
          }}
        >
          <CardMedia
            component="img"
            height="70%"
            image={selectedVariant?.imageUrl ?? ""}
            alt={item.name}
            style={{
              width: "100%",
              objectFit: "contain",
            }}
          />
          <CardContent style={{ flexGrow: 1 }}>
            <Typography variant="h6">{item.name}</Typography>
            <Typography variant="h3">
              {selectedVariant ? (
                item.isSaleActive && selectedVariant.salePrice ? (
                  <>
                    <span style={{ textDecoration: "line-through", marginRight: "10px" }}>
                      LKR {selectedVariant.unitPrice.toFixed(2)}
                    </span>
                    <span style={{ color: "red" }}>
                      LKR {selectedVariant.salePrice.toFixed(2)}
                    </span>
                  </>
                ) : (
                  `LKR ${selectedVariant.unitPrice.toFixed(2)}`
                )
              ) : (
                "Select a variant"
              )}
            </Typography>
            <div
              style={{
                display: "flex",
                justifyContent: "right",
                padding: "0.1px",
              }}
            >
              <Button
                variant="contained"
                color="secondary"
                onClick={handleOpen}
                disabled={
                  quantity > (selectedVariant?.availableQuantity ?? 0) ||
                  !selectedVariant
                }
                style={{ marginRight: "5px" }}
              >
                <ShoppingCartOutlinedIcon sx={{ color: "#DBD8F0" }} />
              </Button>
            </div>
          </CardContent>
        </Card>
      </Box>

      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            bgcolor: "background.paper",
            borderRadius: "20px",
            boxShadow: 24,
            p: 4,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "80%", sm: "50%", md: "30%" },
            maxHeight: { xs: "95vh", sm: "95vh", md: "100vh" }, // Prevent modal from exceeding the viewport height
            display: "flex",
            flexDirection: "column",
            outline: "none",
          }}
        >
          <Typography variant="h4" component="h2">
            {item.name}
          </Typography>
          {/* Image Carousel */}
          <Box
            sx={{
              position: "relative",
              width: "100%",
              mt: "1px",
              mb: "1px",
              height: { xs: "200px", sm: "220px", md: "230px" },
            }}
          >
            <img
              src={images[currentImageIndex]}
              alt={`Image ${currentImageIndex + 1}`}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
            {images.length > 1 && (
              <>
                <IconButton
                  onClick={handlePrevImage}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: 0,
                    transform: "translateY(-50%)",
                    backgroundColor: "rgba(231, 186, 209, 0.5)",
                    color: "white",
                    '&:hover': {
                      backgroundColor: "rgba(127, 190, 235, 0.8)", // Darker on hover
                      color: "#f0f0f0", // Slightly different white
                    }
                  }}
                >
                  <ArrowBackIosIcon />
                </IconButton>
                <IconButton
                  onClick={handleNextImage}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    right: 0,
                    transform: "translateY(-50%)",
                    backgroundColor: "rgba(231, 186, 209, 0.5)",
                    color: "white",
                    '&:hover': {
                      backgroundColor: "rgba(127, 190, 235, 0.8)", // Darker on hover
                      color: "#f0f0f0", // Slightly different white
                    },
                    '&:active': {
                      backgroundColor: "rgba(0, 0, 0, 0.6)", // Adjust for touch
                      color: "#ddd",
                    },
                  }}
                >
                  <ArrowForwardIosIcon />
                </IconButton>
              </>
            )}
          </Box>
          {/* Description for gift_pack */}
          {item.type === "gift_pack" && selectedVariant && (
            <Box>
              <Typography
                variant="h3"
                sx={{ fontWeight: 500, textAlign: "justify" }}
              >
                {selectedVariant.description}
              </Typography>
            </Box>
          )}
          {/* Color Selection */}
          <Box sx={{ mt: { xs: 1, sm: 2, md: 2 }}}>
            <Typography variant="h3" gutterBottom>
              <ChildCareOutlinedIcon
                sx={{
                  fontSize: "1rem",
                  verticalAlign: "middle",
                  marginRight: "8px",
                }}
              />
              Color:
            </Typography>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              {item.variants.map((variant) => (
                <Box
                  key={variant.colorCode}
                  onClick={() => handleColorSelection(variant.colorCode)}
                  sx={{
                    width: "25px",
                    height: "25px",
                    borderRadius: "50%",
                    backgroundColor: variant.colorCode,
                    cursor: "pointer",
                    border:
                      selectedColor === variant.colorCode
                        ? "3px solid #555"
                        : "1px solid #ddd",
                    boxShadow:
                      selectedColor === variant.colorCode
                        ? "0 4px 8px rgba(0, 0, 0, 0.2)"
                        : "none",
                    transition: "all 0.3s ease-in-out",
                  }}
                  title={variant.color} // Tooltip still shows color name
                />
              ))}

              {/* Show only the selected color name */}
              {selectedColor && (
                <Typography
                  sx={{
                    mt: 1,
                    fontWeight: "bold",
                    fontSize: "0.7rem",
                    justifyContent: "right",
                    color: "#E7BAD1",
                    fontStyle: "italic",
                  }}
                >
                  Selected: {" "}
                  {
                    item.variants.find((v) => v.colorCode === selectedColor)?.color || "N/A"
                  }
                </Typography>
              )}
            </Box>
          </Box>


          {/* Variant Description */}
          {item.type !== "gift_pack" && selectedVariant && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h3">
                <ChildCareOutlinedIcon
                  sx={{
                    fontSize: "1rem",
                    verticalAlign: "middle",
                    marginRight: "8px",
                  }}
                />
                <strong>Design:</strong> {selectedVariant.design}
              </Typography>
              <Typography variant="h3">
                <ChildCareOutlinedIcon
                  sx={{
                    fontSize: "1rem",
                    verticalAlign: "middle",
                    marginRight: "8px",
                  }}
                />
                <strong>Material:</strong> {selectedVariant.material}
              </Typography>
              <Typography variant="h3">
                <ChildCareOutlinedIcon
                  sx={{
                    fontSize: "1rem",
                    verticalAlign: "middle",
                    marginRight: "8px",
                  }}
                />
                <strong>Size:</strong> {selectedVariant.size}
              </Typography>
            </Box>
          )}

          {/* Total Price */}
          <Box sx={{ mt: 2, textAlign: "left" }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", color: item.isSaleActive && selectedVariant?.salePrice ? "red" : "#E7BAD1" }}
            >
              {selectedVariant
                ? item.isSaleActive && selectedVariant.salePrice
                  ? `LKR ${(selectedVariant.salePrice * quantity).toFixed(2)}`
                  : `LKR ${(selectedVariant.unitPrice * quantity).toFixed(2)}`
                : "LKR 0.00"}
            </Typography>
            {item.isSaleActive && selectedVariant?.salePrice && (
              <Typography
                variant="body2"
                sx={{ textDecoration: "line-through", color: "gray" }}
              >
                LKR {(selectedVariant.unitPrice * quantity).toFixed(2)}
              </Typography>
            )}
          </Box>
          {/* Quantity Selector */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mt: 1,
              justifyContent: "right",
            }}
          >
            <IconButton onClick={decreaseQuantity} disabled={quantity === 1}>
              <RemoveIcon />
            </IconButton>
            <Typography variant="h3" sx={{ mx: 0.5, fontSize: { xs: "0.9rem", sm: "1.0rem", md: "1.3rem" } }}>
              {quantity}
            </Typography>
            <IconButton
              onClick={increaseQuantity}
              disabled={
                selectedVariant
                  ? quantity === selectedVariant.availableQuantity
                  : false
              }
            >
              <AddIcon />
            </IconButton>
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={handleAddItem}
            disabled={!selectedVariant}
            fullWidth
            sx={{ mt: { xs: 1, sm: 2, md: 2 }, color: "#fff", fontWeight: "550", fontSize: "0.9rem" }}
          >
            Add to Cart <ShoppingCartOutlinedIcon sx={{ pl: "5px" }} />
          </Button>
        </Box>
      </Modal>
    </>
  );
};

export default ItemTile;
